import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GIS_LAYER_SEARCH } from '@rsos/capstone/src/constants/capabilities';
import AccessDenied from '@rsos/components/capstone/AccessDenied';
import BasemapMenu from '@rsos/components/capstone/BasemapMenu';
import { fetchInitialAlertAdr } from '@rsos/components/capstone/DataCards/adrDataSlice';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { ZoomAndLocationContextProvider } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import {
  AGENCY_CHAT,
  ALERTS,
  EMERGENCY_CALLS,
  NON_CALLS,
} from '@rsos/constants/incidentTypes';
import { ESRI } from '@rsos/constants/mapProviders';
import { _911INFORM, _911_INFORM } from '@rsos/constants/spatialLayers';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import useHasAccessToHarmonyPortalChat from '@rsos/hooks/useHasAccessToHarmonyPortalChat';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import useHasPermission from '@rsos/hooks/useHasPermission';
import useIsInteragencyChatEnabled from '@rsos/hooks/useIsInteragencyChatEnabled';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import useMapProvider from '@rsos/hooks/useMapProvider';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import {
  resetSelectedAirAmbulance,
  setHoveredAirAmbulance,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { setPaneOpen } from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  fetchAlertConversationStatus,
  setChatSelectedAlertID,
} from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import {
  fetchAlert,
  removeAlert,
  resetIDWithNewAlert,
  setSelectedAlertID,
  viewAllAlerts,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import {
  removeCall,
  resetNumberWithNewLocation,
  setSelectedCallerID,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  OBLIQUE_IMAGERY,
  setCoordinates,
} from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import {
  setHoveredAlertID,
  setHoveredFrom,
  setHoveredID,
} from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  MEDIA_CARD,
  RADE_MAP_CARD,
  AIR_AMBULANCE_CARD,
  LIVE_VIDEO_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { dismissRcsToast } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { resetWhat3WordsLocation } from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import {
  getMostRecentLoc,
  getMostRecentLocations,
} from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { getCallQueueMaxSize } from '@rsos/utils/metaTags';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePortalType from '@rsos/utils/usePortalType';
import { deactivateSpatialLayerStatus } from '../../actions';
import { forceStaticLayerOn } from '../../actions/spatialLayers';
import { ConversationsProvider } from '../../contexts/ConversationsContext';
import useAllConversationToastNotifications from '../../hooks/useAllConversationToastNotifications';
import useHasAccessToRadioTranscriptions from '../../hooks/useHasAccessToRadioTranscriptions';
import useHasAccessToSummarizationAndKeywords from '../../hooks/useHasAccessToSummarizationAndKeywords.js';
import useInteragencyChatAudioNotifications from '../../hooks/useInteragencyChatAudioNotifications';
import useInteragencyChatToastNotifications from '../../hooks/useInteragencyChatToastNotifications';
import useLoadCallQueueMessages from '../../hooks/useLoadCallQueueMessages';
import useTrackUnreads from '../../hooks/useTrackUnreads';
import {
  setSelectedInteragencyChat,
  useSelectedInteragencyChat,
} from '../../reduxDataStore/interagency/interagencySlice';
import callQueueSelectorNext from '../../selectors/callQueueSelectorNext';
import { persistor } from '../../store';
import { isAdminApproved } from '../../utils/isAdminApproved';
import AgencyShareChatNotifications from '../AgencyShareChat/AgencyShareChatNotifications';
import AlertPane from '../AlertPane';
import AppNotifications from '../AppNotifications';
import ObliqueImageryModal from '../EagleView/modals/ObliqueImageryModal';
import useObliqueImagery from '../EagleView/useObliqueImagery';
import { HarmonyChatWrapper } from '../HarmonyChatWrapper/HarmonyChatWrapper';
import { hide as hideRade } from '../JurisdictionView/RADECard/radeSlice';
import GoogleMap from '../Map';
import MapContainer from '../Map/MapContainer';
import MapLayersControlContainer from '../MapLayersControl/MapLayersControlContainer';
import Queue from '../Queue/Queue';
import ActionBar from './ActionBar/ActionBar';
import AlertToastNotifications from './AlertToastNotifications';
import EventPane from './EventPane';
import { ResourceRequestPanel } from './EventPane/ResourceRequest';
import InteragencyPane from './InteragencyPane';
import {
  FullWidthBox,
  EventDetailsContainer,
  MainContainer,
  SearchAndQueueContainer,
} from './JurisdictionView.styles';
import { KeywordNotifications } from './KeywordNotification/KeywordNotifications';
import GlobalMapSearch from './Map/GlobalMapSearch/GlobalMapSearch';
import { Map as EsriMap } from './Map/Map';
import { isIgnoreType } from './Map/markers/calls/ignoreUtils';
import RadiosPane from './RadiosPane';
import ConnectedUniversalSearchContainer from './UniversalSearch/ConnectedUniversalSearchContainer';
import W3wCallPane from './W3wCallPane';
import {
  isExtraInfoOrMeta,
  shouldRemoveCallFromQueue,
  shouldRemoveAlertFromQueue,
  isSpatialLayerActive,
  check911QueueExceedsMaxSize,
} from './helpers';
import {
  trackAlertMarkerClicks,
  trackCallMarkerClicks,
  trackCloseAlertPane,
  trackClickTab,
  trackClickOnCallFromMap,
} from './jurisdictionViewTrackings';
import {
  useCallHoverTracking,
  useConnectedCarLookup,
} from './useCallHoverTracking';

const JurisdictionView = ({
  hasAgent511Access,
  hasAlertsAccess,
  hasManageAccess,
  isJVEnabled,
  isShareChatEnabled,
}) => {
  const dispatch = useDispatch();
  const { numberWithNewLocation, selectedCallerID, viewNumLocations } =
    useSelector(state => state.emergencyCalls);

  const isChatOpened = useSelector(state => state.harmonyChat.isChatOpened);

  const mapCards = useSelector(state => state.mapCard.cards);
  const isQueueCollapsed = useSelector(state => state.queue?.isQueueCollapsed);

  const what3words = useSelector(state => state.what3words.searched);
  const conversations = useSelector(state => state.textFrom911.conversations);

  const { IDWithNewAlert, selectedAlertID, removedAlerts } = useSelector(
    state => state.incidents,
  );
  const hasAccessToInteragencyChat = useIsInteragencyChatEnabled();
  const hasOpenInteragencyChat =
    useSelectedInteragencyChat() && hasAccessToInteragencyChat;

  const { layout } = useContext(LayoutContext);
  const actionBarRef = useRef();

  const initialQueueTab = () => {
    if (selectedAlertID) return ALERTS;
    if (hasOpenInteragencyChat) return AGENCY_CHAT;
    return EMERGENCY_CALLS;
  };

  const [activeQueueTab, setActiveQueueTab] = useState(initialQueueTab);

  // The InteragencyPane must fully unmount — not just hide via display:none —
  // when the user leaves the Agency Chat queue tab, and on leaving we also drop
  // the selected-conversation state. TalkJS's Chatbox auto-marks messages as
  // read whenever a Chatbox instance is alive on a conversation, independent of
  // whether its DOM ancestors are visible. If we only hide the pane, messages
  // that arrive while the user is on another queue tab (Alerts, 911) will be
  // silently marked read and `session.unreads.onChange` never fires.
  const showInteragencyPane =
    hasOpenInteragencyChat && activeQueueTab === AGENCY_CHAT;

  useEffect(() => {
    if (activeQueueTab !== AGENCY_CHAT && hasOpenInteragencyChat) {
      dispatch(setSelectedInteragencyChat(null));
    }
  }, [activeQueueTab, hasOpenInteragencyChat, dispatch]);
  const [hoveredCall, setHoveredCall] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [removedID, setRemovedID] = useState(null);
  const [showPane, setShowPane] = useState(!!selectedAlertID);
  const [currentERA, setCurrentERA] = useState(null);
  const [showCallPane, setShowCallPane] = useState(!!selectedCallerID);
  const [showW3wCallPane, setShowW3wCallPane] = useState(false);
  const [mafError, setMafError] = useState(null);
  const [adrError, setADRError] = useState(null);
  const [triggerViewJurisdiction, setTriggerViewJurisdiction] = useState(false);
  const [clickedMarkerAnalytics, setClickedMarkerAnalytics] = useState(null);

  const { trackHoverStart, trackHoverEnd } = useCallHoverTracking();
  const isConnectedCarFor = useConnectedCarLookup();
  const { hasCapability: hasGisLayerSearchCapability } =
    useHasCapability(GIS_LAYER_SEARCH);
  const { hasPermission: hasGisLayerSearchPermission } =
    useHasPermission('GIS_LAYER_SEARCH');
  const hasAccessToHarmonyPortalChat = useHasAccessToHarmonyPortalChat();
  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));
  const { profile, isLoggedIn, application, currentOrg } = useSelector(
    state => state.sinatra.user,
  );

  const userProfile = profile;
  const { currentPSAP } = useSelector(state => state.psaps);

  const {
    queryURI,
    irp_access_token,
    irpVersion,
    isIRPmode,
    isIRPresponsiveConditional,
  } = useIsIRP();
  const { isPremiumUser } = usePortalType();

  const exact_polygon = useSelector(
    state => state.geofences.currentGeofence?.exact_polygon || null,
  );

  const expanded_polygon = useSelector(
    state => state.geofences.currentGeofence?.expanded_polygon || null,
  );

  const { allSpatialLayers } = useSelector(state => state.spatialLayers);

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const hasCallStatusAccess = useSelector(state =>
    checkCallStatusAccessSelector({ psaps: state.psaps }),
  );

  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );
  const isTF911Enabled = useIsTF911Enabled();

  const filteredCallsNext = useSelector(state => callQueueSelectorNext(state));

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const themeName = useSelectedTheme();

  const hasLocation = !!(
    selectedCallerID &&
    filteredCalls[selectedCallerID] &&
    (filteredCalls[selectedCallerID].core.locations.length > 0 ||
      filteredCalls[selectedCallerID].core.secondaryLocations.length > 0)
  );

  const activeIncidentID =
    selectedAlertID && !removedAlerts.includes(selectedAlertID)
      ? selectedAlertID
      : selectedCallerID && hasLocation
        ? selectedCallerID
        : null;

  const inactiveIncidentID =
    !hasLocation && selectedCallerID
      ? selectedCallerID
      : selectedAlertID && !removedAlerts.includes(selectedAlertID)
        ? selectedAlertID
        : null;

  // Memoized to stabilize the reference — used as a dependency in the isAllView useMemo below
  const incidents = useMemo(
    () => ({ alerts, calls: filteredCalls }),
    [alerts, filteredCalls],
  );

  const hasProductKey = useSelector(
    state =>
      state.sinatra.products.byName.Capstone ||
      state.sinatra.products.byName['Capstone-Pre-Production'],
  );

  const hasSecondaryLocation = !!useSelector(
    state =>
      !!state.emergencyCalls.selectedCallerID &&
      state.emergencyCalls.callQueue[selectedCallerID] &&
      state.emergencyCalls.callQueue[selectedCallerID].core &&
      state.emergencyCalls.callQueue[selectedCallerID].core
        .secondaryLocations &&
      state.emergencyCalls.callQueue[selectedCallerID].core.secondaryLocations
        .length > 0,
  );

  const hasW3w = !!what3words?.coordinates;

  const hasKeywordsCapability = useHasAccessToSummarizationAndKeywords();
  const shouldHideConnectedUniversalSearch =
    hasGisLayerSearchCapability && hasGisLayerSearchPermission;

  const onViewJurisdiction = () => {
    // will recenter and reset zoom for Map each time
    setTriggerViewJurisdiction(prev => !prev);
  };

  useEffect(() => {
    if (!showW3wCallPane && hasW3w) {
      dispatch(resetWhat3WordsLocation());
    }

    /**
     * @constant
     * @constant {boolean} noConversation - Checks if there are active conversations for the selectedCallerID
     */
    const noConversation =
      isTF911Enabled && selectedCallerID && !conversations[selectedCallerID];

    /**
     * @constant {boolean} noLocation -Checks if there is no primary or secondary location and no selected alert.
     */
    const noLocation =
      !hasLocation && !selectedAlertID && !hasSecondaryLocation;

    /**
     * @constant {boolean} noFilteredCall - Checks if there are filtered calls for the selectedCallerID based on isTF911Enabled
     */
    const noFilteredCall =
      selectedCallerID &&
      !(
        (isTF911Enabled && filteredCallsNext[selectedCallerID]) ||
        (!isTF911Enabled && filteredCalls[selectedCallerID])
      );
    /**
     * Dispatches the action to view all calls if selectedCallerID has no active conversation, no location, and not in filtered call
     */

    if (noConversation && noLocation && noFilteredCall) {
      dispatch(viewAllCalls());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When an authority is created but does not have a verified jurisdiction boundary configured,
  // if an user goes to the data page, the access denied overlay should be displayed.
  const showAccessDeniedOverlay = useMemo(() => {
    let showOverlay = false;

    if (!isJVEnabled) {
      const jurisdictions = currentPSAP?.jurisdictions;

      // The jurisdiction boundary details is not approved yet.
      const pendingJurisdiction =
        hasManageAccess && currentPSAP && !isAdminApproved(jurisdictions);

      if (!hasProductKey || pendingJurisdiction) {
        showOverlay = true;
      }
    }

    return showOverlay;
  }, [currentPSAP, hasManageAccess, hasProductKey, isJVEnabled]);

  /**
   * Handles clicking on a push notification for a call with 911inform data.
   * handleMarkerClick has the same handling for calls
   * @param {Object} options - configurable options required for specific handling
   */
  const handle911informNotificationClick = (options = {}) => {
    const callerId = options?.callerId;
    setActiveQueueTab(EMERGENCY_CALLS);
    dispatch(setSelectedCallerID(callerId));
    dispatch(setSelectedAlertID(null));
    dispatch(setChatSelectedAlertID(null));

    // Assume callerId has spatial layers data if there was a notification
    dispatch(forceStaticLayerOn(_911INFORM));
    if (hasW3w) {
      dispatch(resetWhat3WordsLocation());
    }

    gspx.trackEvent({
      name: 'map_click_911inform_notification',
      type: GPX_EVENT_TYPES.INFO,
      feature: GPX_FEATURES.LEGACY,
      capability: GPX_CAPABILITIES.LEGACY,
      route: GPX_ROUTES.MAP,
      additionalKeys: {
        legacy_category: 'Data Map',
        legacy_name: 'Click on 911inform notification',
        Category: 'Spatial Layer',
        'Launched date': new Date(),
      },
    });
  };

  const handleCloseDataCard = useCallback(() => {
    trackCloseAlertPane(selectedAlertID, irpVersion);
    dispatch(viewAllAlerts());
  }, [selectedAlertID, irpVersion, dispatch]);

  const handleCloseCallPane = () => {
    dispatch(viewAllCalls());
    dispatch(hideRade());
    if (!hasW3w) {
      dispatch(resetWhat3WordsLocation());
      setShowW3wCallPane(false);
    }
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    dispatch(closeCard(LIVE_VIDEO_CARD));
    dispatch(closeCard(AGENT_511_MAP_CARD));
    dispatch(closeCard(MEDIA_CARD));
    dispatch(setPaneOpen(false));
  };

  useEffect(() => {
    setShowCallPane(!!selectedCallerID);
    if (!selectedCallerID) {
      dispatch(closeCard(AGENT_511_MAP_CARD));
      if (hasW3w) setShowW3wCallPane(true);
    }

    // Close RADE and AirAmbulance card if selecting a callerID
    if (selectedCallerID && filteredCallsNext[selectedCallerID]) {
      dispatch(closeCard(RADE_MAP_CARD));
    }
    if (mapCards[AIR_AMBULANCE_CARD]) {
      dispatch(closeCard(AIR_AMBULANCE_CARD));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID]);

  useEffect(() => {
    if (selectedAlertID) {
      setShowPane(true);
      setShowCallPane(false);
      dispatch(hideRade());
      setShowW3wCallPane(false);
    }
    if (hasEsriMapAccess) {
      dispatch(resetWhat3WordsLocation());
    }
  }, [dispatch, selectedAlertID, hasEsriMapAccess]);

  /**
   * Generic handling for hover effect on queue item or marker
   * @param {String} incidentID - Alert or Caller ID
   * @param {String} origin - Where the hover orgin is - 'queue' or 'map'
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   */
  const handleHoverEffect = (incidentID, origin, incidentType, secondary) => {
    const QUEUE = 'queue';

    trackHoverEnd();

    if (incidentID && incidentType === EMERGENCY_CALLS) {
      let mostRecentLocation =
        origin === QUEUE
          ? getMostRecentLoc(filteredCalls[incidentID])
          : filteredCalls?.[incidentID]?.core?.locations?.[0];

      if (secondary) {
        const { mostRecentSecondaryLocation } = getMostRecentLocations(
          filteredCalls[incidentID],
        );
        mostRecentLocation = mostRecentSecondaryLocation;
      }

      // Do not proceed if location isn't found. map markers and callQueue have
      // become mismatched. Only occurs when hovering on marker, not queue item
      // There should also be no hover effect for ignored call/location types
      if (!mostRecentLocation || isIgnoreType(mostRecentLocation)) {
        return;
      }

      const { payloadID } = isExtraInfoOrMeta(mostRecentLocation);

      trackHoverStart({
        callerID: incidentID,
        payloadID,
        route: origin === QUEUE ? GPX_ROUTES.CALL_QUEUE : GPX_ROUTES.MAP,
        callStartTime: mostRecentLocation?.call_start_time,
        irpVersion,
        isConnectedCar: isConnectedCarFor(incidentID),
      });
    }
    dispatch(setHoveredFrom(origin));
    dispatch(setHoveredID(incidentID)); // Only used for Google maps hover handling

    // Esri maps calls/alerts hover handling for markers conflict due to their
    // `else` conditions for when a corresponding marker isn't hovered. This
    // provides more granularity
    if (incidentType === EMERGENCY_CALLS) {
      setHoveredCall(incidentID);
    } else if (incidentType === ALERTS) {
      dispatch(setHoveredAlertID(incidentID));
    } else if (incidentType === NON_CALLS) {
      dispatch(setHoveredAirAmbulance(incidentID));
    }
  };

  /**
   * onClick handler for when clicking on a marker
   * @param {String} incidentID - selectedAlertID or selectedCallerID
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   * @param {Boolean} isAllView - Flag for if user is viewing all alerts/calls or not
   */
  const handleMarkerClick = (incidentID, incidentType, isAllView) => {
    // Note: `setActiveQueueTab` needs to be first in order to properly draw
    // markers when clicking on a marker type that isn't the activeQueueTab
    if (isAllView) {
      setClickedMarkerAnalytics({
        incidentID: incidentID,
        incidentType: incidentType,
      });
      if (incidentType === ALERTS) {
        setActiveQueueTab(ALERTS);
        // viewAllCalls must be dispatched first for MapCard handling
        dispatch(viewAllCalls());
        dispatch(
          fetchAlert({
            currentApplication: application,
            currentOrgName: currentOrg.name,
            alertID: incidentID,
            shouldSetID: true,
          }),
        ).then(() => {
          dispatch(setChatSelectedAlertID(incidentID));
          dispatch(
            fetchAlertConversationStatus({
              orgName: currentOrg.name,
              conversationID: `chat-portal-default-${
                incidentID.split('alert-')[1]
              }`,
            }),
          );
          dispatch(fetchInitialAlertAdr(incidentID));
        });
      } else {
        setActiveQueueTab(EMERGENCY_CALLS);
        trackClickOnCallFromMap(irpVersion);
        // viewAllAlerts must be dispatched first for MapCard handling
        dispatch(viewAllAlerts());
        dispatch(setSelectedCallerID(incidentID));
        dispatch(setChatSelectedAlertID(null));
        dispatch(setHoveredID(null));

        const has911informData =
          !!filteredCalls?.[incidentID]?.additionalData?.[_911_INFORM];
        if (has911informData) {
          dispatch(forceStaticLayerOn(_911INFORM));
        }
      }
      if (hasW3w) {
        dispatch(resetWhat3WordsLocation());
      }

      dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    }
    setHoveredCall(null);
  };

  const handleMapReady = () => {
    setIsMapInitialized(true);
  };

  /**
   * Callback function to handle setting `numberWithNewLocation` and
   * `IDWithNewAlert` to null when a marker has been added based on those
   * values to prevent duplicate markers and losing their reference when
   * switching between views/queues
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   */
  const handleMarkerAdded = incidentType => {
    if (incidentType === EMERGENCY_CALLS) {
      dispatch(resetNumberWithNewLocation());
    } else if (incidentType === ALERTS) {
      dispatch(resetIDWithNewAlert());
    }
  };

  const handleObliqueImageryMarkerClicked = evt => {
    const latLng = evt?.latLng;

    const coor = [
      parseFloat(latLng.lng().toFixed(6)),
      parseFloat(latLng.lat().toFixed(6)),
    ];
    dispatch(setCoordinates({ key: OBLIQUE_IMAGERY, coordinates: coor }));
  };
  /**
   * Callback function for when a push notification is clicked on
   * @param {Object} options - configurable options required for specific handling
   * @param {String} options.handler - flag for which function to map the callback function to
   */
  const handlePushNotificationCallback = (
    options = {
      handler: '',
    },
  ) => {
    const handler = options.handler;
    switch (handler) {
      case _911INFORM: {
        handle911informNotificationClick(options);
        break;
      }
      default: {
        throw new Error('Include a handler value for the options');
      }
    }
  };

  const handleQueueTab = tab => {
    // Reset the views and data tab if the clicked tab is different than the
    // active one to prevent unexpected map behavior
    if (tab !== activeQueueTab) {
      setActiveQueueTab(tab);
    }
    if (tab === ALERTS) {
      trackClickTab('Alerts', irpVersion);
    } else if (tab === AGENCY_CHAT) {
      trackClickTab('Agency Chat', irpVersion);
    } else {
      trackClickTab('911', irpVersion);
    }
  };

  /**
   * Update state to remove an alert or call from the map. Cannot remove a call
   * that is being viewed. Do not update state if viewing another call, since
   * the removedID is used to remove a marker from the map and since the marker
   * doesn't exist when viewing another call, the state is never set back to
   * `null` when a call is removed. Currently only used for calls
   * @param {String} ID - Alert ID or caller ID
   */
  const handleRemoveID = useCallback(
    ID => {
      if (!activeIncidentID) {
        setRemovedID(ID);
      }
    },
    [activeIncidentID],
  );

  const handleCurrentERA = useCallback(payload => {
    setCurrentERA(payload);
  }, []);

  useMemo(() => {
    setCurrentERA(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID, selectedAlertID]);

  //Auto triggers alert tab for irpLogin with an alert ID
  useEffect(() => {
    if (queryURI && queryURI.includes('alert')) {
      setActiveQueueTab(ALERTS);
    }
  }, [queryURI]);

  /**
   * Removes a 911 call from Redux, used for checking inactive 911 calls.
   * @param {string} callerID - caller id in the 911 queue
   */
  const removeCallFromQueue = callerID => {
    // When viewing a call/alert, removedID will not get reset until
    // closing the detailed view, resulting in incorrect marker handling.
    if (!activeIncidentID) setRemovedID(callerID);
    dispatch(removeCall(callerID));
  };

  /**
   * Removes an alert from Redux, used for checking inactive alerts.
   * @param {string} alertID - alert id in the alerts queue
   */
  const removeAlertFromQueue = alertID => {
    // When viewing a call/alert, removedID will not get reset until
    // closing the detailed view, resulting in incorrect marker handling.
    if (!activeIncidentID) setRemovedID(alertID);
    dispatch(removeAlert(alertID));
  };

  /**
   * Deactivates a spatial layer status
   * @param {number} spatialLayedID - spatial layer ID
   */
  const handleDeactivateSpatialLayerStatus = spatialLayedID => {
    dispatch(deactivateSpatialLayerStatus(spatialLayedID));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(filteredCalls).map(callerID => {
        if (
          shouldRemoveCallFromQueue(
            filteredCalls[callerID],
            selectedCallerID,
            hasAgent511Access,
          )
        ) {
          removeCallFromQueue(callerID);
        }
      });

      Object.keys(alerts).map(alertID => {
        if (shouldRemoveAlertFromQueue(alerts[alertID], selectedAlertID)) {
          removeAlertFromQueue(alertID);
        }
      });

      allSpatialLayers.forEach(spatialLayer => {
        if (!isSpatialLayerActive(spatialLayer)) {
          handleDeactivateSpatialLayerStatus(spatialLayer.id);
        }
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents]);

  useEffect(() => {
    if (Object.keys(filteredCalls).length > getCallQueueMaxSize()) {
      check911QueueExceedsMaxSize(
        filteredCalls,
        selectedCallerID,
        removeCallFromQueue,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCalls]);

  const hasLocationsToRender = useHasLocationsToRender();

  const handleQueueItemClicked = id => {
    if (id !== selectedCallerID) {
      dispatch(resetWhat3WordsLocation());
      if (hasEsriMapAccess) {
        setShowW3wCallPane(false);
      }
    }
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    dispatch(closeCard(LIVE_VIDEO_CARD));
    dispatch(resetSelectedAirAmbulance());
    setShowCallPane(true);
  };

  const handleRcsAvailableToastClicked = useCallback(
    callerId => {
      if (callerId !== selectedCallerID) {
        dispatch(setSelectedCallerID(callerId));
        dispatch(resetWhat3WordsLocation());
        if (hasEsriMapAccess) {
          setShowW3wCallPane(false);
        }
      }
      dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));

      setShowCallPane(true);

      dispatch(dismissRcsToast({ callerID: callerId }));
    },
    [selectedCallerID, hasEsriMapAccess, dispatch],
  );

  // Google markers don't currently update handle click
  // If google markers' rendering changes can move into handleMarkerClick again
  useEffect(() => {
    if (clickedMarkerAnalytics?.incidentType) {
      if (clickedMarkerAnalytics.incidentType === ALERTS) {
        trackAlertMarkerClicks(
          activeQueueTab,
          irpVersion,
          clickedMarkerAnalytics.incidentID,
        );
      } else {
        trackCallMarkerClicks(activeQueueTab, irpVersion);
      }
      setClickedMarkerAnalytics(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedMarkerAnalytics]);

  const windowWidth = layout.window.width;

  const isSelectedCallOutboundSMS = useMemo(
    () => filteredCallsNext[selectedCallerID]?.textFrom911?.data?.isOutboundSMS,
    [selectedCallerID, filteredCallsNext],
  );

  const hideQueueConditional =
    !isIRPresponsiveConditional &&
    windowWidth <= BP_LAPTOP &&
    ((selectedCallerID &&
      (hasLocationsToRender || isSelectedCallOutboundSMS)) ||
      selectedAlertID);

  let currentLocation =
    hasLocationsToRender && getMostRecentLoc(filteredCalls[selectedCallerID]);
  if (currentLocation) {
    const { latitude, longitude } = currentLocation;
    currentLocation = { latitude, longitude };
  }

  const isValidCoords = isValidCoordinates(currentLocation);

  let shapes;
  const jurisdictions = currentPSAP?.jurisdictions;
  if (Array.isArray(jurisdictions) && jurisdictions.length > 0) {
    shapes = jurisdictions[0].shapes;
  }

  // Track unread alerts and calls via TalkJS when no alert or call is selected,
  // or when a call is selected but not shared or received via agency share/chat.
  const chatSession = useTrackUnreads();

  // Periodically fetch message history (via `getConversationMessages`) for
  // every conversation tied to a call in the queue and mirror it into
  // redux so GlobalMapSearch can substring-search it.
  useLoadCallQueueMessages();

  // Set initial map provider.
  useMapProvider();

  // load EagleView script
  useObliqueImagery();

  useEffect(() => {
    if (
      isTF911Enabled &&
      !hasLocationsToRender &&
      filteredCallsNext[selectedCallerID]?.textFrom911
    ) {
      onViewJurisdiction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID, hasLocationsToRender]);

  const hasAccessToRadioTranscriptions = useHasAccessToRadioTranscriptions();
  const showRadiosPane = useSelector(state => state.radios.visible);

  const isAllView = useMemo(() => {
    return (
      !activeIncidentID ||
      isIgnoreType(incidents.calls[activeIncidentID]) ||
      (Boolean(incidents.calls[activeIncidentID]) && !isValidCoords)
    );
  }, [activeIncidentID, incidents, isValidCoords]);

  useAllConversationToastNotifications();

  const openInteragencyConversation = useCallback(
    conversationId => {
      // One batched update: the tab switch and the selection land together, so
      // the effect that clears the selection when the Agency Chat tab is not
      // active never sees the intermediate state.
      setActiveQueueTab(AGENCY_CHAT);
      dispatch(setSelectedInteragencyChat(conversationId));
    },
    [dispatch],
  );

  useInteragencyChatToastNotifications({
    session: chatSession,
    isAgencyChatTabActive: activeQueueTab === AGENCY_CHAT,
    openConversation: openInteragencyConversation,
  });

  useInteragencyChatAudioNotifications({
    session: chatSession,
    isAgencyChatTabActive: activeQueueTab === AGENCY_CHAT,
  });

  const isEventPaneCollapsed = useSelector(
    state => state.eventPane.isCollapsed,
  );

  return (
    <ConversationsProvider session={chatSession}>
      <AppNotifications onClickNotification={handlePushNotificationCallback} />
      <AlertToastNotifications />
      {hasKeywordsCapability && <KeywordNotifications />}
      {isShareChatEnabled && (
        <AgencyShareChatNotifications setShowCallPane={setShowCallPane} />
      )}
      <MainContainer isIRP={isIRPresponsiveConditional}>
        {showAccessDeniedOverlay ? (
          <AccessDenied persistor={persistor} />
        ) : (
          <>
            <SearchAndQueueContainer
              isIRP={isIRPresponsiveConditional}
              isCollapsed={isQueueCollapsed}
            >
              {!shouldHideConnectedUniversalSearch ? (
                <ConnectedUniversalSearchContainer
                  data-name="universal-search-container"
                  className="universal-search"
                  filteredCalls={filteredCalls}
                  hasManageAccess={hasManageAccess}
                  setShowW3wCallPane={setShowW3wCallPane}
                  mafError={mafError}
                  setMafError={setMafError}
                  adrError={adrError}
                  setADRError={setADRError}
                  setActiveQueueTab={setActiveQueueTab}
                />
              ) : (
                <div style={{ height: '20px' }} />
                // just a spacer to prevent the action bar from being too close to the nav
              )}
              <ActionBar
                ref={actionBarRef}
                hasAlertsAccess={hasAlertsAccess}
                onQueueTabClick={handleQueueTab}
                shouldHideSearchActionBarItem={
                  isQueueCollapsed && shouldHideConnectedUniversalSearch
                }
              />
              {!hideQueueConditional && (
                <Queue
                  data-name="emergency-queue"
                  activeIncidentID={activeIncidentID}
                  inactiveIncidentID={inactiveIncidentID}
                  activeQueueTab={activeQueueTab}
                  filteredCalls={filteredCallsNext}
                  hoveredCall={hoveredCall}
                  hasAccessToInteragencyChat={hasAccessToInteragencyChat}
                  hasAgent511Access={hasAgent511Access}
                  hasAlertsAccess={hasAlertsAccess}
                  isEmergencyCallsEnabled={true}
                  isJVEnabled={isJVEnabled}
                  irpAlertsID={queryURI}
                  selectedAlertID={selectedAlertID}
                  selectedCallerID={selectedCallerID}
                  setActiveQueueTab={setActiveQueueTab}
                  setShowPane={setShowPane}
                  onQueueHover={handleHoverEffect}
                  onQueueTabClick={handleQueueTab}
                  onRemoveID={handleRemoveID}
                  onQueueItemClicked={handleQueueItemClicked}
                  setRemovedAlertID={setRemovedID}
                  hasEsriMapAccess={hasEsriMapAccess}
                  onViewJurisdiction={onViewJurisdiction}
                  actionBarRef={actionBarRef}
                  onViewRcsEvent={handleRcsAvailableToastClicked}
                />
              )}
            </SearchAndQueueContainer>

            <FullWidthBox isViewingAlerts={!!selectedAlertID}>
              <>
                <EventDetailsContainer
                  isIRP={isIRPresponsiveConditional}
                  selectedItem={selectedAlertID || selectedCallerID}
                  isAllCallsSelected={!selectedCallerID}
                >
                  {selectedAlertID && showPane && (
                    <AlertPane
                      onClose={handleCloseDataCard}
                      onRemoveID={handleRemoveID}
                    />
                  )}
                  {filteredCallsNext[selectedCallerID] &&
                    (isMapInitialized || irp_access_token) &&
                    showCallPane && (
                      <EventPane
                        callerID={selectedCallerID}
                        hasW3w={hasW3w}
                        onActionableButton={handleCurrentERA}
                        onClose={handleCloseCallPane}
                      />
                    )}

                  {hasAccessToRadioTranscriptions &&
                    (isMapInitialized || irp_access_token) &&
                    showRadiosPane && <RadiosPane />}

                  {!selectedAlertID &&
                    (!selectedCallerID ||
                      (selectedCallerID && !hasLocationsToRender)) &&
                    hasW3w &&
                    showW3wCallPane &&
                    !showCallPane &&
                    !showInteragencyPane && (
                      <W3wCallPane
                        hasLocationsToRender={hasLocationsToRender}
                        hasW3w={hasW3w}
                      />
                    )}
                  {showInteragencyPane && <InteragencyPane />}

                  <MapContainer
                    selectedAlertID={selectedAlertID}
                    isIRP={isIRPresponsiveConditional}
                    selectedItem={selectedAlertID || selectedCallerID}
                    isEventPaneCollapsed={isEventPaneCollapsed}
                  >
                    {shouldHideConnectedUniversalSearch && <GlobalMapSearch />}
                    <BasemapMenu />
                    {isMapInitialized && <MapLayersControlContainer />}
                    {(!irp_access_token || isIRPmode) && isLoggedIn ? (
                      <ZoomAndLocationContextProvider>
                        {mapProvider &&
                          (mapProvider === ESRI ? (
                            <EsriMap
                              activeQueueTab={activeQueueTab}
                              currentERA={currentERA}
                              hoveredCall={hoveredCall}
                              onMapReady={handleMapReady}
                              onMarkerAdd={handleMarkerAdded}
                              onMarkerClick={handleMarkerClick}
                              onMarkerHover={handleHoverEffect}
                              onRemoveID={handleRemoveID}
                              removedID={removedID}
                              themeName={themeName}
                              setMafError={setMafError}
                              setADRError={setADRError}
                              triggerViewJurisdiction={triggerViewJurisdiction}
                            />
                          ) : (
                            <GoogleMap
                              activeQueueTab={activeQueueTab}
                              activeIncidentID={activeIncidentID}
                              boundingBox={shapes}
                              currentERA={currentERA}
                              inactiveIncidentID={inactiveIncidentID}
                              hasW3w={hasW3w}
                              IDWithNewAlert={IDWithNewAlert}
                              incidents={incidents}
                              incidentType={
                                selectedAlertID
                                  ? ALERTS
                                  : selectedCallerID
                                    ? EMERGENCY_CALLS
                                    : // Markers only understands EMERGENCY_CALLS and ALERTS.
                                      // AGENCY_CHAT is not a valid marker type, so fall back to
                                      // EMERGENCY_CALLS to prevent markers[incidentType] from
                                      // being undefined when the interagency pane is open.
                                      activeQueueTab === AGENCY_CHAT
                                      ? EMERGENCY_CALLS
                                      : activeQueueTab
                              }
                              isAllView={isAllView}
                              mapView={'Jurisdiction'}
                              numberWithNewLocation={numberWithNewLocation}
                              onMapReady={handleMapReady}
                              onMarkerAdd={handleMarkerAdded}
                              onMarkerClick={handleMarkerClick}
                              onMarkerHover={handleHoverEffect}
                              onRemoveID={handleRemoveID}
                              polygon={exact_polygon}
                              expandedPolygon={expanded_polygon}
                              removedID={removedID}
                              setMafError={setMafError}
                              setADRError={setADRError}
                              themeName={themeName}
                              userProfile={userProfile}
                              viewNumLocations={viewNumLocations}
                              triggerViewJurisdiction={triggerViewJurisdiction}
                              filteredCalls={filteredCallsNext}
                              hasCallStatus={
                                hasCallStatusAccess && hasAniAliAccess
                              }
                              handleObliqueImageryMarkerClicked={
                                handleObliqueImageryMarkerClicked
                              }
                            />
                          ))}
                      </ZoomAndLocationContextProvider>
                    ) : null}
                  </MapContainer>

                  {hasAccessToHarmonyPortalChat && isChatOpened && (
                    <HarmonyChatWrapper />
                  )}
                  <ResourceRequestPanel />
                </EventDetailsContainer>
              </>
            </FullWidthBox>
            <ObliqueImageryModal />
          </>
        )}
      </MainContainer>
    </ConversationsProvider>
  );
};

export default React.memo(JurisdictionView);
